import moment from 'moment';
import {useEffect, useState} from 'react';
import * as Redux from 'react-redux';
import {usePageText} from '../context/page-text-context';
import {useRepair} from '../context/repair-context';
import {timeWindowSelected} from '../features/scheduling/schedulerSlice';
import {displayTime, displayTimeWindows} from '../utils/scheduled-window';
import {ConfirmButton, ConfirmButtonFilled} from './buttons';
import {Loading} from './loading';
import Modal from './modal';
import {CalendarSVG, LocationSVG} from './svgs';
import Time from './time';

function RescheduleForm({error, handleHideModal, handleReschedule, schedulingIsLoading}) {
  const {
    time: {onSiteTechnician, rescheduleForm, scheduleHeader, allTimesAreShownIn},
    cancelButton,
    confirmButton,
  } = usePageText();
  const {
    repair: {
      address,
      timeZone,
      storeName = '',
      selectedStore: {storeId = ''},
      isPending,
      scheduledWindowStart,
      scheduledWindowEnd,
      preciseTimes: customerPreciseTime,
    },
  } = useRepair();
  const dispatch = Redux.useDispatch();
  const {start, end} = Redux.useSelector((state) => state.scheduler);
  const [errorModalVisible, setErrorModalVisible] = useState(false);
  const [timeRefetchSignal, setTimeRefetchSignal] = useState(null);
  const newStartTime = moment.tz(start, timeZone);
  const newEndTime = moment.tz(end, timeZone);
  const existingStartTime = moment.tz(scheduledWindowStart, timeZone);
  const existingEndTime = moment.tz(scheduledWindowEnd, timeZone);

  const handleConfirmErrorModal = () => {
    dispatch(timeWindowSelected({start: null, end: null, utcOffset: null, appointmentId: null}));
    setErrorModalVisible(false);
    setTimeRefetchSignal((prev) => prev + 1); // setTimeRefetchSignal to trigger times refetch in Time
  };

  useEffect(() => {
    if (!schedulingIsLoading && error) {
      setErrorModalVisible(!!error);
    }
  }, [error, schedulingIsLoading]);

  const outputServiceTime = (_start, _end) => {
    if (!_start.isValid()) {
      return '-';
    }

    return customerPreciseTime
      ? displayTime({
          time: _start,
          timeZone,
        })
      : displayTimeWindows({
          start: _start,
          end: _end,
          timeZone: timeZone,
        });
  };

  return (
    <div className="flex flex-col gap-4 w-full">
      <h1 className="text-center text-xl font-bold text-black">
        {isPending ? rescheduleForm.headerPending : rescheduleForm.headerNew}
      </h1>
      <p className="text-center text-sm">
        ({allTimesAreShownIn} {moment().tz(timeZone).format('z')}.)
      </p>
      <div className="flex flex-col lg:flex-row gap-4">
        <div className="border border-light-gray rounded-lg flex-1 h-fit">
          <Time appointmentPickerOnly={true} step={''} refetchSignal={timeRefetchSignal} />
        </div>

        <div className="repair-container  ">
          {/* Repair address */}
          <div>
            <h3 className="text-black font-bold sm:text-lg">{rescheduleForm.repairLocation}</h3>
            <span className="flex py-2 w-full gap-4">
              <LocationSVG />
              <span>
                <h4>{storeName}</h4>
                <p>{address}</p>
              </span>
            </span>
            <hr className="border-light-gray my-4" />
          </div>
          {/* Current repair time */}
          <div>
            {isPending ? null : (
              <div className="">
                <h3 className="text-black font-bold sm:text-lg">{rescheduleForm.currentAppt}</h3>
                <span className="flex py-2 w-full gap-4">
                  <CalendarSVG />
                  {existingStartTime.isValid() ? (
                    <div className="">
                      <p className="">{outputServiceTime(existingStartTime, existingEndTime)}</p>
                    </div>
                  ) : null}
                </span>
                <hr className="border-light-gray my-4" />
              </div>
            )}

            {/* New repair time */}
            <div className="">
              <h3 className="text-black font-bold sm:text-lg">
                {' '}
                {isPending ? rescheduleForm.scheduleHeader : rescheduleForm.newAppt}{' '}
              </h3>
              <span className="flex py-2 w-full gap-4">
                <CalendarSVG />
                {newStartTime.isValid() ? null : <h3>{scheduleHeader}</h3>}
                {start ? (
                  <p className="text-blue">{outputServiceTime(newStartTime, newEndTime)}</p>
                ) : isPending ? (
                  <h4>-</h4>
                ) : (
                  <p> {rescheduleForm.selectAppt}</p>
                )}
              </span>
            </div>
            <i className="text-xs">
              {isPending && storeId === null
                ? onSiteTechnician
                : storeId === null
                  ? rescheduleForm.pendingNullStore
                  : storeId !== null && ''}
            </i>
          </div>
          <div className="spacer" />
          <div>
            <Modal handleModalClose={() => {}} showCloseButton={false} visible={errorModalVisible}>
              <div className="error-modal text-red flex flex-col gap-4 justify-center items-center max-w-[600px]">
                <div>{error}</div>
                <div className="min-w-[150px]">
                  <ConfirmButtonFilled onClick={handleConfirmErrorModal}>Ok</ConfirmButtonFilled>
                </div>
              </div>
            </Modal>
          </div>
        </div>
      </div>
      <div className="flex gap-2 my-2 justify-center w-full max-w-[300px] self-center">
        {schedulingIsLoading ? (
          <Loading />
        ) : (
          <>
            <ConfirmButton onClick={handleHideModal}>{cancelButton}</ConfirmButton>
            <ConfirmButtonFilled disabled={!start} onClick={handleReschedule} id="btn-confirm">
              {confirmButton}
            </ConfirmButtonFilled>
          </>
        )}
      </div>
    </div>
  );
}

export default RescheduleForm;
